import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, LinearProgress, Alert, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails, FormControlLabel, Switch,
} from '@mui/material';
import {
  AccountBalance, Add, Edit, Delete, Download, CheckCircle,
  ExpandMore, Send, Pending, Lock,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const FORMATS: Record<string, { label: string; desc: string }> = {
  hsbc_csv:      { label: 'HSBC CSV',      desc: 'HSBC Business Internet Banking Bulk Credit' },
  hang_seng_csv: { label: 'Hang Seng CSV', desc: 'Hang Seng Business e-Banking Bulk Fund Transfer' },
  standard_csv:  { label: 'Standard CSV',  desc: 'Generic bank CSV — name, code, account, amount' },
  ach_txt:       { label: 'ACH Text',      desc: 'CHATS/ACH fixed-width text format' },
};

const STATUS_COLOR: Record<string, any> = { draft: 'default', approved: 'warning', sent: 'success' };
const ITEM_STATUS_COLOR: Record<string, any> = { included: 'success', excluded: 'default', failed: 'error' };

function fmtAmount(n: number | string) {
  return `HK$${Number(n || 0).toLocaleString('en-HK', { minimumFractionDigits: 2 })}`;
}

function fmtDate(s?: string) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface BankFileRecord {
  id: string; name: string; payrollMonth: string; format: string; status: string;
  payerAccountNo?: string; payerAccountName?: string; payerBankCode?: string;
  totalAmount: number; totalItems: number; valueDate?: string;
  approvedBy?: string; approvedAt?: string; sentAt?: string; notes?: string;
  items?: BankFileItem[];
}
interface BankFileItem {
  id: string; bankFileId: string; employeeId: string; payeeName?: string;
  bankCode?: string; bankName?: string; accountNo?: string; accountType?: string;
  netSalary: number; reference?: string; status: string; notes?: string;
  employee?: { firstName: string; lastName: string };
}
interface Stats { totalFiles: number; totalSent: number; totalDraft: number; totalDisbursed: number; lastFile?: BankFileRecord; }

function StatCard({ label, value, color, icon }: any) {
  return (
    <Card sx={{ textAlign: 'center', p: 1 }}>
      <CardContent>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );
}

export default function BankFilePage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager', 'finance'].includes(user?.role ?? '');

  const [files, setFiles] = useState<BankFileRecord[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedData, setExpandedData] = useState<Record<string, BankFileRecord>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [genDialog, setGenDialog] = useState(false);
  const [genForm, setGenForm] = useState({
    payrollMonth: new Date().toISOString().slice(0, 7),
    format: 'hsbc_csv',
    valueDate: '',
    payerAccountNo: '',
    payerAccountName: '',
    payerBankCode: '004',
    notes: '',
  });

  const [editDialog, setEditDialog] = useState<Partial<BankFileRecord> | null>(null);
  const [editItemDialog, setEditItemDialog] = useState<{ fileId: string; item: Partial<BankFileItem> } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [f, s] = await Promise.all([apiFetch('/api/bank-files'), apiFetch('/api/bank-files/stats')]);
      setFiles(f); setStats(s);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadExpanded = async (id: string) => {
    if (expandedData[id]) return;
    try {
      const d = await apiFetch(`/api/bank-files/${id}`);
      setExpandedData(p => ({ ...p, [id]: d }));
    } catch {}
  };

  const handleExpand = (id: string) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    loadExpanded(id);
  };

  const generate = async () => {
    try {
      await apiFetch('/api/bank-files/generate', { method: 'POST', body: JSON.stringify(genForm) });
      setGenDialog(false); load();
    } catch (e: any) { setError(e.message); }
  };

  const approve = async (id: string) => {
    try { await apiFetch(`/api/bank-files/${id}/approve`, { method: 'POST' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const markSent = async (id: string) => {
    if (!window.confirm('Mark this file as sent to the bank? This cannot be undone.')) return;
    try { await apiFetch(`/api/bank-files/${id}/sent`, { method: 'POST' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const deleteFile = async (id: string) => {
    if (!window.confirm('Delete this bank file?')) return;
    try { await apiFetch(`/api/bank-files/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const downloadFile = (id: string) => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/bank-files/${id}/export`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', '');
    // For auth we need to fetch first
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.click();
        URL.revokeObjectURL(u);
      }).catch(e => setError(e.message));
  };

  const saveEdit = async () => {
    if (!editDialog?.id) return;
    try {
      await apiFetch(`/api/bank-files/${editDialog.id}`, { method: 'PATCH', body: JSON.stringify(editDialog) });
      setEditDialog(null); load(); setExpandedData({});
    } catch (e: any) { setError(e.message); }
  };

  const saveItemEdit = async () => {
    if (!editItemDialog) return;
    try {
      await apiFetch(`/api/bank-files/${editItemDialog.fileId}/items/${editItemDialog.item.id}`,
        { method: 'PATCH', body: JSON.stringify(editItemDialog.item) });
      setEditItemDialog(null);
      setExpandedData({});
      loadExpanded(editItemDialog.fileId);
    } catch (e: any) { setError(e.message); }
  };

  const currentMonth = new Date().toISOString().slice(0, 7);
  const lastDayOfMonth = new Date(
    parseInt(genForm.payrollMonth.split('-')[0]),
    parseInt(genForm.payrollMonth.split('-')[1]),
    0,
  ).toISOString().slice(0, 10);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance sx={{ fontSize: 32, color: '#1b5e20' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Payroll Bank File Generator</Typography>
            <Typography variant="body2" color="text.secondary">
              Generate HSBC / Hang Seng bulk payment files for salary disbursement
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#1b5e20' }}
          onClick={() => { setGenForm(p => ({ ...p, valueDate: lastDayOfMonth })); setGenDialog(true); }}>
          Generate Bank File
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="Total Files" value={stats.totalFiles} icon={<AccountBalance />} color="#1b5e20" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="Sent to Bank" value={stats.totalSent} icon={<Send />} color="#1565c0" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="Pending Approval" value={stats.totalDraft} icon={<Pending />} color="#e65100" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="Total Disbursed" value={fmtAmount(stats.totalDisbursed)} icon={<CheckCircle />} color="#2e7d32" />
          </Grid>
        </Grid>
      )}

      {/* File list */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {files.length === 0 && !loading ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <AccountBalance sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography>No bank files generated yet. Generate your first payroll bank file above.</Typography>
        </Paper>
      ) : (
        files.map(f => {
          const detail = expandedData[f.id];
          return (
            <Accordion key={f.id} expanded={expanded === f.id} onChange={() => handleExpand(f.id)}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={700}>{f.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {f.payrollMonth} · {FORMATS[f.format]?.label} · {f.totalItems} employees
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography fontWeight={700} color="#1b5e20">{fmtAmount(f.totalAmount)}</Typography>
                    <Chip label={f.status} size="small" color={STATUS_COLOR[f.status] ?? 'default'} />
                    {f.valueDate && (
                      <Typography variant="caption" color="text.secondary">Value: {fmtDate(f.valueDate)}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }} onClick={e => e.stopPropagation()}>
                    {f.status === 'draft' && (
                      <Tooltip title="Approve">
                        <IconButton size="small" color="warning" onClick={() => approve(f.id)}><CheckCircle fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {f.status === 'approved' && (
                      <Tooltip title="Mark as Sent to Bank">
                        <IconButton size="small" color="primary" onClick={() => markSent(f.id)}><Send fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Download bank file">
                      <IconButton size="small" color="success" onClick={() => downloadFile(f.id)}><Download fontSize="small" /></IconButton>
                    </Tooltip>
                    {f.status !== 'sent' && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setEditDialog({ ...f })}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => deleteFile(f.id)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </>
                    )}
                    {f.status === 'sent' && <Lock sx={{ fontSize: 16, color: '#9e9e9e', ml: 0.5 }} />}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* Payer info */}
                <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                  {f.payerAccountName && <Typography variant="body2"><b>Payer:</b> {f.payerAccountName}</Typography>}
                  {f.payerAccountNo   && <Typography variant="body2"><b>Account:</b> {f.payerAccountNo}</Typography>}
                  {f.payerBankCode    && <Typography variant="body2"><b>Bank Code:</b> {f.payerBankCode}</Typography>}
                  {f.approvedBy       && <Typography variant="body2"><b>Approved by:</b> {f.approvedBy} ({fmtDate(f.approvedAt)})</Typography>}
                  {f.sentAt           && <Typography variant="body2"><b>Sent:</b> {fmtDate(f.sentAt)}</Typography>}
                </Box>

                {/* Items table */}
                {detail ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><b>Employee</b></TableCell>
                          <TableCell><b>Bank</b></TableCell>
                          <TableCell><b>Account No</b></TableCell>
                          <TableCell><b>Amount</b></TableCell>
                          <TableCell><b>Reference</b></TableCell>
                          <TableCell><b>Status</b></TableCell>
                          {f.status !== 'sent' && <TableCell></TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(detail.items ?? []).map(item => (
                          <TableRow key={item.id} hover
                            sx={{ opacity: item.status === 'excluded' ? 0.5 : 1 }}>
                            <TableCell><Typography fontWeight={600}>{item.payeeName}</Typography></TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.bankCode}</Typography>
                              <Typography variant="caption" color="text.secondary">{item.bankName}</Typography>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{item.accountNo || '—'}</TableCell>
                            <TableCell><Typography fontWeight={600} color="#1b5e20">{fmtAmount(item.netSalary)}</Typography></TableCell>
                            <TableCell><Typography variant="caption">{item.reference}</Typography></TableCell>
                            <TableCell>
                              <Chip label={item.status} size="small" color={ITEM_STATUS_COLOR[item.status] ?? 'default'} />
                            </TableCell>
                            {f.status !== 'sent' && (
                              <TableCell>
                                <Tooltip title="Edit item">
                                  <IconButton size="small"
                                    onClick={() => setEditItemDialog({ fileId: f.id, item: { ...item } })}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                    <LinearProgress sx={{ maxWidth: 200, mx: 'auto' }} />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      {/* ── GENERATE DIALOG ── */}
      <Dialog open={genDialog} onClose={() => setGenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Payroll Bank File</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info">
              Pulls all <b>processed / paid</b> payroll records for the selected month and creates a bank
              transfer file ready for upload to your bank portal.
            </Alert>
            <TextField label="Payroll Month" type="month" fullWidth InputLabelProps={{ shrink: true }}
              value={genForm.payrollMonth}
              onChange={e => setGenForm(p => ({ ...p, payrollMonth: e.target.value }))} />
            <TextField select label="Bank File Format" fullWidth value={genForm.format}
              onChange={e => setGenForm(p => ({ ...p, format: e.target.value }))}>
              {Object.entries(FORMATS).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Box>
                    <Typography>{v.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{v.desc}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Value Date (settlement date)" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={genForm.valueDate}
              onChange={e => setGenForm(p => ({ ...p, valueDate: e.target.value }))} />
            <Typography variant="subtitle2" fontWeight={700}>Payer (Company) Bank Details</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Bank Code" sx={{ width: 120 }} value={genForm.payerBankCode}
                onChange={e => setGenForm(p => ({ ...p, payerBankCode: e.target.value }))}
                helperText="e.g. 004=HSBC" />
              <TextField label="Account No" fullWidth value={genForm.payerAccountNo}
                onChange={e => setGenForm(p => ({ ...p, payerAccountNo: e.target.value }))} />
            </Box>
            <TextField label="Account Name" fullWidth value={genForm.payerAccountName}
              onChange={e => setGenForm(p => ({ ...p, payerAccountName: e.target.value }))} />
            <TextField label="Notes" fullWidth multiline rows={2} value={genForm.notes}
              onChange={e => setGenForm(p => ({ ...p, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenDialog(false)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#1b5e20' }} onClick={generate}>Generate</Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT FILE DIALOG ── */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Bank File</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="File Name" fullWidth value={editDialog?.name ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, name: e.target.value }))} />
            <TextField label="Value Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={editDialog?.valueDate ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, valueDate: e.target.value }))} />
            <TextField label="Payer Account Name" fullWidth value={editDialog?.payerAccountName ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, payerAccountName: e.target.value }))} />
            <TextField label="Payer Account No" fullWidth value={editDialog?.payerAccountNo ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, payerAccountNo: e.target.value }))} />
            <TextField label="Notes" fullWidth multiline rows={2} value={editDialog?.notes ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#1b5e20' }} onClick={saveEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT ITEM DIALOG ── */}
      <Dialog open={!!editItemDialog} onClose={() => setEditItemDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Payment Item — {editItemDialog?.item.payeeName}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Bank Code" fullWidth value={editItemDialog?.item.bankCode ?? ''}
              onChange={e => setEditItemDialog(p => p ? { ...p, item: { ...p.item, bankCode: e.target.value } } : null)} />
            <TextField label="Account No" fullWidth value={editItemDialog?.item.accountNo ?? ''}
              onChange={e => setEditItemDialog(p => p ? { ...p, item: { ...p.item, accountNo: e.target.value } } : null)} />
            <TextField label="Net Salary (HKD)" type="number" fullWidth value={editItemDialog?.item.netSalary ?? 0}
              onChange={e => setEditItemDialog(p => p ? { ...p, item: { ...p.item, netSalary: Number(e.target.value) } } : null)} />
            <TextField select label="Status" fullWidth value={editItemDialog?.item.status ?? 'included'}
              onChange={e => setEditItemDialog(p => p ? { ...p, item: { ...p.item, status: e.target.value } } : null)}>
              <MenuItem value="included">Included</MenuItem>
              <MenuItem value="excluded">Excluded (pay separately)</MenuItem>
              <MenuItem value="failed">Failed / Returned</MenuItem>
            </TextField>
            <TextField label="Notes" fullWidth value={editItemDialog?.item.notes ?? ''}
              onChange={e => setEditItemDialog(p => p ? { ...p, item: { ...p.item, notes: e.target.value } } : null)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItemDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#1b5e20' }} onClick={saveItemEdit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
